import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import type { SaveSmtpSettingsRequest, SmtpSettingsDto } from '../../types';
import './styles.css';

const SECURITY_OPTIONS = [
    { value: 'None', label: 'Без шифрования' },
    { value: 'StartTls', label: 'STARTTLS' },
    { value: 'SslOnConnect', label: 'SSL/TLS' }
];

const SettingsManagement: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [hasPassword, setHasPassword] = useState(false);
    const [form, setForm] = useState<SaveSmtpSettingsRequest>({
        host: '',
        port: 587,
        security: 'StartTls',
        username: '',
        password: '',
        fromEmail: '',
        fromName: ''
    });

    useEffect(() => {
        let isMounted = true;
        api.get<{ settings: SmtpSettingsDto }>('/api/admin/settings/smtp')
            .then(response => {
                if (!isMounted || !response.data) return;
                const settings = response.data.settings;
                setForm({
                    host: settings.host,
                    port: settings.port,
                    security: settings.security,
                    username: settings.username,
                    password: '',
                    fromEmail: settings.fromEmail,
                    fromName: settings.fromName
                });
                setHasPassword(settings.hasPassword);
            })
            .catch(() => {})
            .finally(() => {
                if (isMounted) setLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    const updateField = <K extends keyof SaveSmtpSettingsRequest>(field: K, value: SaveSmtpSettingsRequest[K]) =>
        setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        if (!form.host.trim() || !form.fromEmail.trim()) {
            toast.error('Заполните SMTP-сервер и адрес отправителя');
            return;
        }

        setSaving(true);
        try {
            const response = await api.put<{ settings: SmtpSettingsDto }>('/api/admin/settings/smtp', {
                ...form,
                host: form.host.trim(),
                fromEmail: form.fromEmail.trim(),
                fromName: form.fromName.trim()
            });
            if (response.data) {
                setHasPassword(response.data.settings.hasPassword);
            }
            updateField('password', '');
            toast.success('Настройки сохранены');
        } catch {
            // ошибки показывает apiClient
        } finally {
            setSaving(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail.trim()) {
            toast.error('Укажите адрес получателя');
            return;
        }

        setTesting(true);
        try {
            await api.post<{ message: string }>('/api/admin/settings/smtp/test', { to: testEmail.trim() });
            toast.success('Тестовое письмо отправлено');
        } catch {
            // ошибки показывает apiClient
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="settings-management container-fluid">
            <div className="settings-panel">
                <h4>
                    <i className="bi bi-envelope-at"></i> SMTP
                </h4>
                <p className="settings-panel-hint">
                    Используются для отправки почтовых сообщений, например писем о заказах менеджерам.
                </p>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Загрузка...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="form-grid">
                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-host">
                                    <i className="bi bi-server"></i> Сервер
                                </label>
                                <input
                                    id="smtp-host"
                                    type="text"
                                    className="form-input-custom"
                                    value={form.host}
                                    onChange={(event) => updateField('host', event.target.value)}
                                    placeholder="smtp.example.com"
                                />
                            </div>

                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-port">
                                    <i className="bi bi-hash"></i> Порт
                                </label>
                                <input
                                    id="smtp-port"
                                    type="number"
                                    min={1}
                                    max={65535}
                                    className="form-input-custom"
                                    value={form.port}
                                    onChange={(event) => updateField('port', Number(event.target.value) || 0)}
                                />
                            </div>

                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-security">
                                    <i className="bi bi-shield-check"></i> Шифрование
                                </label>
                                <select
                                    id="smtp-security"
                                    className="form-input-custom form-select-custom"
                                    value={form.security}
                                    onChange={(event) => updateField('security', event.target.value)}
                                >
                                    {SECURITY_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-username">
                                    <i className="bi bi-person"></i> Логин
                                </label>
                                <input
                                    id="smtp-username"
                                    type="text"
                                    className="form-input-custom"
                                    value={form.username}
                                    onChange={(event) => updateField('username', event.target.value)}
                                    placeholder="user@example.com"
                                />
                            </div>

                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-password">
                                    <i className="bi bi-key"></i> Пароль
                                </label>
                                <input
                                    id="smtp-password"
                                    type="password"
                                    className="form-input-custom"
                                    value={form.password ?? ''}
                                    onChange={(event) => updateField('password', event.target.value)}
                                    placeholder={hasPassword ? 'Сохранён, оставьте пустым чтобы не менять' : ''}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-from-email">
                                    <i className="bi bi-envelope"></i> Адрес отправителя (от)
                                </label>
                                <input
                                    id="smtp-from-email"
                                    type="email"
                                    className="form-input-custom"
                                    value={form.fromEmail}
                                    onChange={(event) => updateField('fromEmail', event.target.value)}
                                    placeholder="noreply@example.com"
                                />
                            </div>

                            <div className="form-group-custom">
                                <label className="form-label-custom" htmlFor="smtp-from-name">
                                    <i className="bi bi-type"></i> Имя отправителя
                                </label>
                                <input
                                    id="smtp-from-name"
                                    type="text"
                                    className="form-input-custom"
                                    value={form.fromName}
                                    onChange={(event) => updateField('fromName', event.target.value)}
                                    placeholder="Lira"
                                />
                            </div>
                        </div>

                        <div className="settings-actions">
                            <button
                                type="button"
                                className="btn-primary-custom"
                                onClick={() => void handleSave()}
                                disabled={saving}
                            >
                                {saving ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                                Сохранить
                            </button>
                        </div>
                    </>
                )}
            </div>
            <div className="settings-panel">
                <h4>
                    <i className="bi bi-send"></i> Проверка
                </h4>
                <p className="settings-panel-hint">Отправить тестовое письмо с сохранёнными настройками.</p>

                <div className="form-grid">
                    <div className="form-group-custom">
                        <label className="form-label-custom" htmlFor="smtp-test-email">
                            <i className="bi bi-envelope-open"></i> Получатель
                        </label>
                        <input
                            id="smtp-test-email"
                            type="email"
                            className="form-input-custom"
                            value={testEmail}
                            onChange={(event) => setTestEmail(event.target.value)}
                            placeholder="you@example.com"
                        />
                    </div>
                </div>

                <div className="settings-actions">
                    <button
                        type="button"
                        className="btn-primary-custom"
                        onClick={() => void handleTestEmail()}
                        disabled={testing || loading}
                    >
                        {testing ? <span className="btn-spinner"></span> : <i className="bi bi-send"></i>}
                        Отправить тестовое письмо
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettingsManagement;